import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toDisplayScore } from '@cercle/shared';
import { useApi } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { Avatar, Badge, EmptyState, Kicker, Muted, ScoreMedallion, Skeleton } from '@/shared/ui';
import { formatShortDate } from './format';
import styles from './recap.module.scss';

/** Liste des séances passées — la cinquième entrée « Classements » de la bottom nav. */
export function SessionsListScreen(): ReactNode {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.sessions.list(), []);

  if (loading) return <Skeleton />;
  if (error) return <EmptyState>{error}</EmptyState>;

  const closed = data?.filter((session) => session.status === 'CLOSED') ?? [];
  if (closed.length === 0) {
    return <EmptyState>Aucune séance clôturée pour l’instant. Le classement viendra.</EmptyState>;
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Classements</h1>
      <ul className={styles.sessionList}>
        {closed.map((session) => (
          <li key={session.id}>
            <Link to={`/seances/${session.id}/recap`} className={styles.sessionRow}>
              <span className={styles.number}>n°{session.number}</span>
              <span className={styles.sessionInfo}>
                <strong>{formatShortDate(session.startsAt)}</strong>
                <Muted>
                  {session.rums.length} rhum{session.rums.length > 1 ? 's' : ''}
                  {session.location ? ` · ${session.location}` : ''}
                </Muted>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** US-17 — récapitulatif d'une séance : podium, coup de cœur, notes par membre. */
export function RecapScreen(): ReactNode {
  const { id = '' } = useParams();
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.sessions.recap(id), [id]);

  if (loading) return <Skeleton />;
  if (error || !data) return <EmptyState>{error ?? 'Récapitulatif introuvable.'}</EmptyState>;

  return (
    <div className={styles.page}>
      <header>
        <Kicker>Séance n°{data.number}</Kicker>
        <h1 className={styles.title}>{formatShortDate(data.startsAt)}</h1>
      </header>

      <section>
        <h2 className={styles.sectionTitle}>Podium</h2>
        {data.podium.length === 0 ? (
          <EmptyState>Aucune note validée sur cette séance.</EmptyState>
        ) : (
          <ol className={styles.podium}>
            {data.podium.map((entry, index) => (
              <li key={entry.rum.id} style={{ animationDelay: `${index * 90}ms` }}>
                <span className={styles.rank}>{entry.rank}</span>
                <span className={styles.podiumInfo}>
                  <strong>{entry.rum.name}</strong>
                  <Muted>{[entry.rum.country, entry.rum.style].filter(Boolean).join(' · ')}</Muted>
                </span>
                {entry.rum.id === data.clubFavouriteRumId ? <Badge>Coup de cœur</Badge> : null}
                <ScoreMedallion score={entry.average} />
              </li>
            ))}
          </ol>
        )}
      </section>

      {data.bestComment ? (
        <section>
          <h2 className={styles.sectionTitle}>La plume de la soirée</h2>
          <blockquote className={styles.quote}>
            « {data.bestComment.text} »
            <footer>
              <Muted>
                {data.bestComment.memberPseudo} · {data.bestComment.rumName}
              </Muted>
            </footer>
          </blockquote>
        </section>
      ) : null}

      <section>
        <h2 className={styles.sectionTitle}>Notes par membre</h2>
        <ul className={styles.members}>
          {data.perMember.map((entry) => (
            <li key={entry.member.id}>
              <Avatar pseudo={entry.member.pseudo} url={entry.member.avatarUrl} />
              <span>{entry.member.pseudo}</span>
              <strong>{entry.average === null ? '—' : toDisplayScore(entry.average)}</strong>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
